import * as React from 'react';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  /** Icon component from lucide-react. Renders 28px on the left. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Override the icon tint. Defaults to text-primary; platform-admin pages
   *  use rose/amber to telegraph "you are operating outside the normal app". */
  iconClassName?: string;
  /** Right-aligned action slot. Typically a Button or cluster. */
  action?: React.ReactNode;
  /** Override heading level for accessibility nesting. Defaults to 1. */
  as?: 'h1' | 'h2';
  className?: string;
}

/**
 * Top-of-page heading block. Promoted from 30+ hand-rolled headers that
 * disagreed on tracking-tight, heading level, and icon sizing.
 *
 * `flex-wrap` is deliberate — on narrow viewports the action cluster needs
 * to wrap below the title rather than crush it.
 */
export function PageHeader({
  title,
  subtitle,
  icon: Icon,
  iconClassName,
  action,
  as = 'h1',
  className,
}: PageHeaderProps) {
  const Heading = as;

  return (
    <div className={cn('mb-4 flex flex-wrap items-center gap-2', className)}>
      {Icon && <Icon className={cn('h-7 w-7 text-primary', iconClassName)} />}
      <div className="min-w-0">
        <Heading className="text-2xl font-semibold tracking-tight">{title}</Heading>
        {subtitle && (
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {action && <div className="ml-auto flex items-center gap-1">{action}</div>}
    </div>
  );
}
